import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * PaymentReturn
 * 
 * Handles Stripe's return from onboarding.
 * Two modes:
 *   - /payments/return   → after successful onboarding
 *   - /payments/refresh  → when the onboarding link expired
 * 
 * Both redirect to the vendor's dashboard with a query param
 * that triggers a status sync in PaymentSetup.
 * 
 * Determines which dashboard to return to based on the user's role.
 */
export default function PaymentReturn({ mode = 'complete' }) {
  const navigate = useNavigate();
  const { user, availableRoles } = useAuth();

  useEffect(() => {
    // Figure out which dashboard to return to
    const role = user?.role;
    let target = '/buyer/dashboard';

    if (role === 'producer') target = '/producer-dashboard';
    else if (role === 'hub') target = '/hub-dashboard';
    else if (role === 'pickup') target = '/pickup-dashboard';
    else if (role === 'educator') target = '/educator-dashboard';
    else if (role === 'admin') target = '/admin';
    // If the user has multiple roles, prefer the vendor role if they have one
    else if (availableRoles?.includes('producer')) target = '/producer-dashboard';
    else if (availableRoles?.includes('hub')) target = '/hub-dashboard';

    // Small delay so the sync has a chance to complete
    const timer = setTimeout(() => {
      navigate(`${target}?onboarding=${mode}`, { replace: true });
    }, 1500);

    return () => clearTimeout(timer);
  }, [navigate, user, availableRoles, mode]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="text-4xl mb-4">
          {mode === 'complete' ? '✅' : '🔄'}
        </div>
        <h2 className="text-xl font-semibold text-gray-800">
          {mode === 'complete' ? 'Setup complete' : 'Refreshing setup'}
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          {mode === 'complete'
            ? 'Checking your account status...'
            : 'Your link expired. Returning to your dashboard...'}
        </p>
        <div className="mt-4 text-gray-400 text-sm">
          ⏳ This will take just a moment
        </div>
      </div>
    </div>
  );
}
